/**
 * Applies accepted schema suggestions onto the proposal baseline (PRE-2722).
 *
 * Mirrors the server-side reference implementation in
 * data-parser-service/svc/v1/profiling/proposal.py::apply_accepted.
 *
 * The guarantee this module exists to hold: with nothing accepted, the output
 * is the baseline the user would have got by defining the schema by hand. That
 * is why suggestions carry a `fieldPatch` and this is a shallow merge, rather
 * than the UI re-deriving field semantics.
 */

import { SUGGESTION_STATUS } from "store/schemaProfile/actions";

/**
 * Types whose min/max constrain the numeric value rather than string length.
 * Kept in sync with the noMinMaxTypes list in SchemaModels.js so an applied
 * proposal cannot produce a field the create API will reject.
 */
const NO_MIN_MAX_TYPES = [
    "date",
    "datetime",
    "time",
    "date-year",
    "date-month",
    "date-day",
    "boolean",
    "picklist",
    "latlng",
    "attachment",
    "image",
    "document",
    "video",
];

/**
 * Closed vocabularies the schema create/update API validates against
 * (schema-service api/request_schemas/v1/createFieldSchema.js). These are Joi
 * `.valid(...)` lists, so an out-of-vocabulary value fails the whole request
 * rather than being dropped from it.
 *
 * data-parser-service is responsible for only ever proposing legal values
 * (svc/v1/profiling/platform_formats.py). This list is the second line of
 * defence: a profiler that regresses, or an older deployed parser paired with
 * a newer front end, must not be able to turn an accepted suggestion into a
 * failed schema create. Unknown values are dropped, never forwarded.
 */
const SCHEMA_DATE_FORMATS = [
    "DD$MM$YYYY",
    "DD$MMM$YYYY",
    "MM$DD$YYYY",
    "MMM$DD$YYYY",
    "MM$DD$YY",
    "YYYY$MM$DD",
    "YYYY$MMM$DD",
    "YY$MM$DD",
    "YYMMDD",
    "YYYYMMDD",
];
const SCHEMA_DATE_SEPARATORS = ["-", "/", ".", " "];
const SCHEMA_TIME_FORMATS = ["hh:mm:ss a", "hh:mm:ss A", "hh:mm a", "hh:mm A", "HH:mm:ss", "HH:mm"];
const SCHEMA_BOOLEAN_STYLES = ["y/n", "Y/N", "1/0", "t/f", "T/F"];

const FORMAT_VOCABULARIES = {
    dateFormat: SCHEMA_DATE_FORMATS,
    dateSeparator: SCHEMA_DATE_SEPARATORS,
    timeFormat: SCHEMA_TIME_FORMATS,
    allowedBooleanValues: SCHEMA_BOOLEAN_STYLES,
};

/**
 * Remove format attributes the schema API would reject. Mutates and returns
 * the field. `typeKey` differs by path: the proposal shape uses `dataType`,
 * the connection wizard's field shape uses `type`.
 */
function dropUnsupportedFormats(field, typeKey) {
    Object.keys(FORMAT_VOCABULARIES).forEach((attribute) => {
        const value = field[attribute];
        if (value === undefined || value === null) return;
        if (!FORMAT_VOCABULARIES[attribute].includes(value)) {
            delete field[attribute];
        }
    });

    // A separator without a format it belongs to is meaningless, and the API
    // strips both for any other type anyway.
    const dataType = field[typeKey];
    if (dataType !== "date" && dataType !== "datetime") {
        delete field.dateFormat;
        delete field.dateSeparator;
    } else if (field.dateSeparator && !field.dateFormat) {
        delete field.dateSeparator;
    }
    if (dataType !== "datetime" && dataType !== "time") {
        delete field.timeFormat;
    }
    if (dataType !== "boolean") {
        delete field.allowedBooleanValues;
    }
    return field;
}

/**
 * Merge the accepted suggestions for one field onto its baseline.
 *
 * Suggestions are applied in proposal order so a base-type patch lands before
 * the constraint patches that depend on it.
 */
export function applyAcceptedToField(field, suggestionStatuses = {}) {
    const applied = { ...field.baseline };
    const acceptedIds = [];

    (field.suggestions || []).forEach((suggestion) => {
        const status = suggestionStatuses[suggestion.id];
        if (status !== SUGGESTION_STATUS.ACCEPTED) return;
        acceptedIds.push(suggestion.id);
        Object.assign(applied, suggestion.fieldPatch || {});
    });

    // Strip bounds the schema API does not accept for the resulting type. This
    // can happen legitimately: accepting a LENGTH_RANGE and then a BASE_TYPE of
    // date leaves a min/max that no longer means anything.
    if (NO_MIN_MAX_TYPES.includes(applied.dataType)) {
        delete applied.min;
        delete applied.max;
    }

    dropUnsupportedFormats(applied, "dataType");

    return { field: applied, acceptedIds };
}

/**
 * Build the field list for the schema create form from a proposal.
 * Returns fields in proposal order, so column order matches the source.
 */
export function applyAcceptedSuggestions(schemaProfile, suggestionStatuses = {}) {
    if (!schemaProfile || !schemaProfile.fields) return { fields: [], acceptedCount: 0 };

    let acceptedCount = 0;
    const fields = schemaProfile.fields.map((field) => {
        const { field: applied, acceptedIds } = applyAcceptedToField(field, suggestionStatuses);
        acceptedCount += acceptedIds.length;
        return applied;
    });

    return { fields, acceptedCount };
}

/**
 * Picklist candidates route into the PRE-2699 accelerator rather than being
 * applied here (PRE-2720). Returns the accepted candidates so the wizard can
 * hand them off with their profile evidence.
 */
export function collectAcceptedPicklistCandidates(schemaProfile, suggestionStatuses = {}) {
    if (!schemaProfile || !schemaProfile.fields) return [];

    const candidates = [];
    schemaProfile.fields.forEach((field) => {
        (field.suggestions || []).forEach((suggestion) => {
            if (suggestion.category !== "PICKLIST_CANDIDATE") return;
            if (suggestionStatuses[suggestion.id] !== SUGGESTION_STATUS.ACCEPTED) return;
            candidates.push({
                fieldName: field.name,
                suggestionId: suggestion.id,
                ...(suggestion.detail || {}),
            });
        });
    });
    return candidates;
}

/**
 * Merge accepted suggestions onto fields that already have a type.
 *
 * The file path starts from the proposal's ``baseline`` (always ``string``,
 * because a CSV carries no type metadata). A data connection is different: the
 * connector already reports real types, which this wizard maps to Pretectum
 * types before the user ever sees them. Applying the profile baseline here
 * would *downgrade* a connector-typed ``integer`` column back to ``string``,
 * so on this path suggestions enrich the existing fields rather than replacing
 * them.
 *
 * Only accepted suggestions are applied, so with nothing accepted the fields
 * are returned exactly as the connector described them.
 */
export function mergeSuggestionsIntoFields(fields, schemaProfile, suggestionStatuses = {}) {
    if (!schemaProfile || !schemaProfile.fields) {
        return { fields, acceptedCount: 0 };
    }

    const profileByName = new Map(schemaProfile.fields.map((field) => [field.name, field]));
    let acceptedCount = 0;

    const merged = fields.map((field) => {
        const profiled = profileByName.get(field.name);
        if (!profiled) return field;

        const enriched = { ...field };
        (profiled.suggestions || []).forEach((suggestion) => {
            if (suggestionStatuses[suggestion.id] !== SUGGESTION_STATUS.ACCEPTED) return;
            acceptedCount += 1;

            const patch = suggestion.fieldPatch || {};
            // The wizard's field shape uses `type`, not `dataType`.
            if (patch.dataType) enriched.type = patch.dataType;
            if (patch.min !== undefined) enriched.min = patch.min;
            if (patch.max !== undefined) enriched.max = patch.max;
            if (patch.dateFormat) enriched.dateFormat = patch.dateFormat;
            if (patch.dateSeparator) enriched.dateSeparator = patch.dateSeparator;
            if (patch.timeFormat) enriched.timeFormat = patch.timeFormat;
            if (patch.allowedBooleanValues) enriched.allowedBooleanValues = patch.allowedBooleanValues;
        });

        if (NO_MIN_MAX_TYPES.includes(enriched.type)) {
            delete enriched.min;
            delete enriched.max;
        }
        dropUnsupportedFormats(enriched, "type");
        return enriched;
    });

    return { fields: merged, acceptedCount };
}

export { NO_MIN_MAX_TYPES };

